const listSeparator = ',';
const encodedListSeparator = encodeURIComponent(listSeparator);

/**
 * The list separator must be decoded twice to not conflict with the
 * decoding and encoding of URLSearchParams
 */
const normalizeDecodeParamValue = (decodedValue: string) => {
	const normalized = decodedValue.replaceAll(
		encodedListSeparator,
		listSeparator,
	);
	return normalized;
};

/**
 * The list separator must be decoded twice to not conflict with the
 * decoding and encoding of URLSearchParams
 */
const decodeParamValue = (value: string) => {
	const decoded = decodeURIComponent(value);
	const normalized = normalizeDecodeParamValue(decoded);
	return normalized;
};

/**
 * The list separator must be encoded twice to not conflict with the
 * decoding and encoding of URLSearchParams
 */
const encodeParamValue = (value: string) => {
	const normalized = value.replaceAll(listSeparator, encodedListSeparator);
	const encoded = encodeURIComponent(normalized);
	return encoded;
};

const decodeString = function* (
	string: string,
): Iterable<[string, Array<string>]> {
	const paramsString = string.startsWith('?') ? string.slice(1) : string;
	for (const entry of paramsString.split('&')) {
		const [key, values] = entry.split('=');

		if (!key || !values) {
			continue;
		}
		const paramKey = decodeURIComponent(key);
		const paramValues = values.split(listSeparator).map(decodeParamValue);

		yield [paramKey, paramValues];
	}
};

const decodeURLSearchParams = function* (
	searchParams: URLSearchParams,
): Iterable<[string, Array<string>]> {
	// URLSearchParams has already decoded the key and value
	for (const [key, value] of searchParams) {
		const paramValues = value
			.split(listSeparator)
			// URLSearchParams has already decoded the value
			.map(normalizeDecodeParamValue);

		if (!key || paramValues.length === 0) {
			continue;
		}

		yield [key, paramValues];
	}
};

const initParams = (entries: Iterable<[string, string | Array<string>]>) => {
	const params = new Map<string, string[]>();

	for (const [key, value] of entries) {
		const paramValues = Array.isArray(value) ? value : [value];

		const existingValues = params.get(key);

		const combinedValues = existingValues
			? existingValues.concat(paramValues)
			: paramValues;

		params.set(key, combinedValues);
	}

	return params;
};

/** Use comma separated lists in URL Search Params for entries with multiple values */
export class CommaURLSearchParams implements URLSearchParams {
	get [Symbol.toStringTag]() {
		return 'URLSearchParams';
	}
	*[Symbol.iterator](): URLSearchParamsIterator<[string, string]> {
		for (const [key, values] of this.#params.entries()) {
			for (const value of values) {
				yield [key, value];
			}
		}
	}
	#params: Map<string, string[]>;
	constructor(
		init?:
			| string
			| Iterable<[string, string | Array<string>]>
			| URLSearchParams
			| Record<string, string | Array<string>>,
	) {
		if (init == null) {
			this.#params = new Map();
			return;
		}
		if (typeof init === 'string') {
			this.#params = initParams(decodeString(init));
			return;
		}
		if (init instanceof URLSearchParams) {
			this.#params = initParams(decodeURLSearchParams(init));
			return;
		}
		if (!(Symbol.iterator in init)) {
			this.#params = initParams(Object.entries(init));
			return;
		}
		this.#params = initParams(init);
	}

	append(name: string, value: string): void {
		const values = this.#params.get(name) ?? [];
		values.push(value);
		this.#params.set(name, values);
	}
	delete(name: string): void {
		this.#params.delete(name);
	}
	entries(): URLSearchParamsIterator<[string, string]> {
		return this[Symbol.iterator]();
	}
	get(name: string): string | null {
		return this.#params.get(name)?.[0] ?? null;
	}
	getAll(name: string): Array<string> {
		return this.#params.get(name) ?? [];
	}
	has(name: string): boolean {
		return this.#params.has(name);
	}
	keys(): URLSearchParamsIterator<string> {
		return this.#params.keys();
	}
	set(name: string, value: string): void {
		this.#params.set(name, [value]);
	}
	sort(): void {
		this.#params = new Map([...this.#params].sort());
	}
	toString(): string {
		const entries = [...this.#params];

		const string = entries
			.map(([key, values]) => {
				return [
					encodeParamValue(key),
					values.map(encodeParamValue).join(listSeparator),
				].join('=');
			})
			.join('&');

		return string;
	}
	*values(): URLSearchParamsIterator<string> {
		for (const [_key, value] of this) {
			yield value;
		}
	}
	get size() {
		return this.#params.size;
	}
	forEach(
		callback: (value: string, key: string, parent: URLSearchParams) => void,
		thisArg?: unknown,
	): void {
		for (const [key, value] of this) {
			callback.call(thisArg, value, key, this);
		}
	}
}
